import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [error, setError] = useState(null);
  const [particles, setParticles] = useState([]);
  const containerRef = useRef(null);
  const location = useLocation();

  // Generate floating particles
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        duration: Math.random() * 20 + 10,
        delay: Math.random() * 5,
      }));
      setParticles(newParticles);
    };
    generateParticles();
  }, []);

  // Check for error from AuthCallback redirect
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Handle mouse movement for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleGoogleLogin = () => {
    setIsLoading(true);
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen w-full flex items-center justify-center p-4 md:p-8 overflow-hidden relative"
      style={{ background: '#0a0a0f' }}
    >
      {/* Animated Grid Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 240, 255, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        {/* Radial gradient overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, #0a0a0f 70%)'
          }}
        />
      </div>

      {/* Floating Particles */}
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            background: 'rgba(0, 240, 255, 0.6)',
            boxShadow: '0 0 6px rgba(0, 240, 255, 0.8)',
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.3, 0.8, 0.3],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Animated background glow */}
      <motion.div
        className="absolute w-[800px] h-[800px] rounded-full opacity-20 blur-[150px]"
        style={{
          background: 'radial-gradient(circle, #00f0ff 0%, transparent 70%)',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.15, 0.25, 0.15],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Scanline Effect */}
      <div 
        className="absolute inset-0 pointer-events-none z-50"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 240, 255, 0.02) 2px, rgba(0, 240, 255, 0.02) 4px)',
        }}
      />

      {/* Moving Scanline */}
      <motion.div
        className="absolute left-0 right-0 h-[2px] pointer-events-none z-50"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.3), transparent)',
        }}
        animate={{
          top: ['0%', '100%'],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Main container card */}
      <motion.div 
        className="relative w-full max-w-5xl overflow-hidden flex flex-col md:flex-row rounded-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          background: 'rgba(10, 15, 20, 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 240, 255, 0.2)',
          boxShadow: '0 0 40px rgba(0, 240, 255, 0.1), inset 0 0 60px rgba(0, 240, 255, 0.03)',
        }}
      >
        {/* HUD Corner Brackets */}
        <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-[#00f0ff]/60" />
        <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-[#00f0ff]/60" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-[#00f0ff]/60" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-[#00f0ff]/60" />

        {/* Left Side - Login Form */}
        <div className="w-full md:w-[45%] p-8 md:p-12 flex flex-col justify-center relative z-10">
          {/* Logo with Orb */}
          <div className="flex items-center gap-4 mb-10">
            {/* Animated Mini Orb */}
            <div className="relative w-12 h-12">
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, #00f0ff, #0080ff, #004080)',
                  boxShadow: '0 0 20px rgba(0, 240, 255, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.2)',
                }}
                animate={{
                  scale: [1, 1.05, 1],
                  boxShadow: [
                    '0 0 20px rgba(0, 240, 255, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.2)',
                    '0 0 30px rgba(0, 240, 255, 0.7), inset 0 0 25px rgba(255, 255, 255, 0.3)',
                    '0 0 20px rgba(0, 240, 255, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.2)',
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
              {/* Orbiting ring */}
              <motion.div
                className="absolute inset-[-4px] rounded-full border border-[#00f0ff]/40"
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
              />
            </div>
            <div>
              <span className="text-2xl font-bold text-white tracking-wider">O.R.I.O.N</span>
              <div className="text-[#00f0ff]/60 text-xs font-mono tracking-widest">COMMAND CENTER</div>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-wide">
            System Access
          </h1>
          <p className="text-[#00f0ff]/60 mb-8 font-mono text-sm">
            Initialize secure connection protocol
          </p>

          {/* Error Message */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 rounded-lg flex items-center gap-3" 
              style={{
                background: 'rgba(255, 50, 50, 0.1)',
                border: '1px solid rgba(255, 50, 50, 0.3)',
              }}
              data-testid="auth-error"
            >
              <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-red-400 font-mono text-sm flex-1">{error}</p>
              <button 
                onClick={() => setError(null)} 
                className="text-red-400/60 hover:text-red-400 transition-colors"
                data-testid="dismiss-error"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </motion.div>
          )}

          {/* Google Login Button */}
          <motion.button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            data-testid="google-login-btn"
            className="w-full py-4 px-6 rounded-lg flex items-center justify-center gap-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-6 relative overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1) 0%, rgba(0, 128, 255, 0.1) 100%)',
              border: '1px solid rgba(0, 240, 255, 0.3)',
            }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)',
            }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Button glow effect */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.2) 0%, rgba(0, 128, 255, 0.2) 100%)',
              }}
            />
            {isLoading ? (
              <motion.div 
                className="w-5 h-5 border-2 border-[#00f0ff] border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            ) : (
              <>
                <svg className="w-5 h-5 relative z-10" viewBox="0 0 24 24">
                  <path fill="#00f0ff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#00d4aa" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#00b8ff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#0099ff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-[#00f0ff] font-medium tracking-wide relative z-10">Initialize Google Auth</span>
              </>
            )}
          </motion.button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#00f0ff]/30 to-transparent" />
            <span className="text-[#00f0ff]/40 text-xs font-mono">SECURE PROTOCOL</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#00f0ff]/30 to-transparent" />
          </div>

          {/* Enterprise SSO Notice */}
          <div 
            className="p-4 rounded-lg"
            style={{
              background: 'rgba(0, 240, 255, 0.05)',
              border: '1px solid rgba(0, 240, 255, 0.15)',
            }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.2) 0%, rgba(0, 128, 255, 0.2) 100%)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                }}
              >
                <svg className="w-5 h-5 text-[#00f0ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <div className="font-medium text-white text-sm">Enterprise Authentication</div>
                <div className="text-[#00f0ff]/60 text-xs font-mono">
                  Access restricted to <span className="text-[#00f0ff]">@wellversed.in</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-[#00f0ff]/10">
            <p className="text-center text-xs text-[#00f0ff]/30 font-mono">
              ORION v2.0 // ENCRYPTED CONNECTION // WELLVERSED SYSTEMS
            </p>
          </div>
        </div>

        {/* Right Side - Image with Parallax */}
        <div className="w-full md:w-[55%] relative min-h-[300px] md:min-h-[600px] overflow-hidden">
          {/* Parallax Image Container */}
          <motion.div
            className="absolute inset-0"
            animate={{
              x: mousePosition.x * -20,
              y: mousePosition.y * -20,
              scale: 1.1
            }}
            transition={{ type: "spring", stiffness: 100, damping: 30 }}
          >
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/6cae129b-b8a0-4946-bf45-3db0ebe28c2c/images/7dcbd82f21fd021c6f05b4918a0acdc39818848d6bee15c85eae96de60acfca1.png"
              alt="AI Command Center"
              className="w-full h-full object-cover"
            />
          </motion.div>

          {/* Cyan gradient overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1) 0%, transparent 50%, rgba(0, 10, 20, 0.8) 100%)',
            }}
          />
          
          {/* Grid overlay on image */}
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `
                linear-gradient(rgba(0, 240, 255, 0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 240, 255, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '30px 30px',
            }}
          />

          {/* Floating HUD elements with parallax */}
          <motion.div
            className="absolute top-10 right-10 w-20 h-20 rounded-full backdrop-blur-md"
            style={{
              background: 'rgba(0, 240, 255, 0.1)',
              border: '1px solid rgba(0, 240, 255, 0.3)',
              boxShadow: '0 0 20px rgba(0, 240, 255, 0.2)',
            }}
            animate={{
              x: mousePosition.x * 30,
              y: mousePosition.y * 30,
            }}
            transition={{ type: "spring", stiffness: 100, damping: 30 }}
          />
          
          <motion.div
            className="absolute top-1/3 left-10 w-12 h-12 rounded-lg backdrop-blur-md"
            style={{
              background: 'rgba(0, 240, 255, 0.1)',
              border: '1px solid rgba(0, 240, 255, 0.2)',
            }}
            animate={{
              x: mousePosition.x * -40,
              y: mousePosition.y * -40,
              rotate: mousePosition.x * 10
            }}
            transition={{ type: "spring", stiffness: 100, damping: 30 }}
          />

          {/* Status indicator */}
          <motion.div 
            className="absolute top-6 right-6 px-4 py-2 rounded-full backdrop-blur-md flex items-center gap-2"
            style={{
              background: 'rgba(0, 20, 30, 0.8)',
              border: '1px solid rgba(0, 240, 255, 0.3)',
            }}
            animate={{
              x: mousePosition.x * 15,
              y: mousePosition.y * 15,
            }}
            transition={{ type: "spring", stiffness: 150, damping: 30 }}
          >
            <motion.div 
              className="w-2 h-2 rounded-full bg-[#00f0ff]"
              animate={{
                opacity: [1, 0.5, 1],
                scale: [1, 1.2, 1],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-[#00f0ff] text-xs font-mono">ONLINE</span>
          </motion.div>

          {/* Bottom text */}
          <motion.div 
            className="absolute bottom-8 left-8 right-8"
            animate={{
              x: mousePosition.x * -10,
              y: mousePosition.y * -10,
            }}
            transition={{ type: "spring", stiffness: 150, damping: 30 }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-white leading-tight">
              Welcome to the
            </h2>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: '#00f0ff' }}>
              Command <span className="italic">Center</span>
            </h2>
            <div className="mt-4 flex items-center gap-2">
              <div className="h-px flex-1 bg-gradient-to-r from-[#00f0ff]/60 to-transparent" />
              <span className="text-[#00f0ff]/40 text-xs font-mono">JARVIS PROTOCOL ACTIVE</span>
            </div>
          </motion.div>

          {/* HUD corners on image section */}
          <div className="absolute top-4 left-4 w-12 h-12 border-l border-t border-[#00f0ff]/40" />
          <div className="absolute top-4 right-4 w-12 h-12 border-r border-t border-[#00f0ff]/40" />
          <div className="absolute bottom-4 left-4 w-12 h-12 border-l border-b border-[#00f0ff]/40" />
          <div className="absolute bottom-4 right-4 w-12 h-12 border-r border-b border-[#00f0ff]/40" />
        </div>
      </motion.div>

      {/* Bottom corner decoration */}
      <div className="absolute bottom-4 left-4 text-[#00f0ff]/30 text-xs font-mono flex items-center gap-2">
        <motion.div 
          className="w-2 h-2 rounded-full bg-[#00f0ff]/50"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        ORION v2.0 // Powered by Wellversed AI
      </div>

      {/* Top right system status */}
      <div className="absolute top-4 right-4 text-[#00f0ff]/30 text-xs font-mono">
        SYS_STATUS: NOMINAL
      </div>
    </div>
  );
};

export default Login;
